import React from 'react';
import { Row, Col, Card, Badge, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const unitLabels = {
  Leads: { icon: 'user-plus', color: '#2c7be5' },
  Clicks: { icon: 'mouse-pointer', color: '#00d97e' },
  Impresiones: { icon: 'eye', color: '#27bcfd' },
  WhatsApp: { icon: 'comments', color: '#25D366' },
  'Vistas de Video': { icon: 'play-circle', color: '#6b5eae' },
  Reach: { icon: 'bullhorn', color: '#f68c1f' },
  CPA: { icon: 'dollar-sign', color: '#e63757' },
  CPM: { icon: 'chart-bar', color: '#f5803e' },
  CPV: { icon: 'play-circle', color: '#6b5eae' },
};

const getStatusConfig = (pct, isCostMetric) => {
  if (pct >= 90 && pct <= 110) return { bg: 'success', label: 'En meta', variant: 'success' };
  if (pct > 110) {
    return isCostMetric
      ? { bg: 'danger', label: 'Sobre costo', variant: 'danger' }
      : { bg: 'info', label: 'Superado', variant: 'info' };
  }
  if (pct >= 70) return { bg: 'warning', label: 'En riesgo', variant: 'warning' };
  return { bg: 'danger', label: 'Crítico', variant: 'danger' };
};

const formatValue = (val, unit) => {
  if (unit?.includes('CP') || unit === 'CPA' || unit === 'CPM' || unit === 'CPV') {
    return '$' + (val || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M';
  if (val >= 1000) return (val / 1000).toFixed(1) + 'K';
  return (val || 0).toLocaleString();
};

const ObjectiveCard = ({ objective }) => {
  const pct = objective.progressPct || 0;
  const status = getStatusConfig(pct, objective.isCostMetric);
  const unitInfo = unitLabels[objective.unit] || { icon: 'bullseye', color: '#748194' };
  const clampedPct = Math.min(pct, 100);

  return (
    <Card className="h-100 shadow-sm border-0 hover-shadow" style={{ transition: 'box-shadow 0.2s' }}>
      <Card.Body className="p-3">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center me-2"
              style={{ width: 36, height: 36, backgroundColor: `${unitInfo.color}20` }}
            >
              <FontAwesomeIcon icon={unitInfo.icon} style={{ color: unitInfo.color, fontSize: 14 }} />
            </div>
            <div>
              <h6 className="mb-0 fs-11 text-truncate" style={{ maxWidth: 140 }}>
                {objective.clientName}
              </h6>
              <small className="text-muted fs-10">{objective.accountName}</small>
            </div>
          </div>
          <Badge bg={status.bg} className="fs-10">{status.label}</Badge>
        </div>

        <div className="mb-2">
          <small className="text-uppercase text-500 fw-bold fs-10">{objective.unit}</small>
        </div>

        <div className="d-flex justify-content-between mb-1">
          <div>
            <small className="text-muted d-block fs-10">Objetivo</small>
            <span className="fw-bold fs-11">{formatValue(objective.nominalValue, objective.unit)}</span>
          </div>
          <div className="text-center">
            <small className="text-muted d-block fs-10">Esperado</small>
            <span className="fw-bold fs-11 text-primary">{formatValue(objective.expectedValue, objective.unit)}</span>
          </div>
          <div className="text-end">
            <small className="text-muted d-block fs-10">Real</small>
            <span className={`fw-bold fs-11 text-${status.variant}`}>
              {formatValue(objective.realValue, objective.unit)}
            </span>
          </div>
        </div>

        <ProgressBar
          now={clampedPct}
          variant={status.variant}
          style={{ height: 6, borderRadius: 3 }}
          className="mt-2"
        />
        <div className="text-end mt-1">
          <small className={`fw-bold text-${status.variant} fs-10`}>{pct.toFixed(1)}%</small>
        </div>
      </Card.Body>
    </Card>
  );
};

const ObjectivesProgressCard = ({ objectives = [], maxItems = 6 }) => {
  const displayed = objectives.slice(0, maxItems);

  if (displayed.length === 0) {
    return (
      <Card className="shadow-sm border-0">
        <Card.Body className="text-center py-4">
          <FontAwesomeIcon icon="bullseye" size="2x" className="text-300 mb-2" />
          <p className="text-muted mb-0">No hay objetivos activos en este período</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Row className="g-3">
      {displayed.map((obj) => (
        <Col xs={12} md={6} lg={4} key={obj.id}>
          <ObjectiveCard objective={obj} />
        </Col>
      ))}
    </Row>
  );
};

export default ObjectivesProgressCard;
